import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, FormControl, InputLabel, Select, MenuItem, Grid,
  IconButton, Snackbar, Alert, RadioGroup, FormControlLabel, Radio, SelectChangeEvent, CircularProgress
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { ROUTES } from 'routes/constants';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { CuffaCreditLineEnum, CuffaCreditTypeEnum, CuffaFinancialInstitutionEnum } from 'interfaces/cuffa.interface';

// --- Interfaces ---
interface CreditData {
  institution: string;
  creditLine: string;
  creditType: string;
  amount: string;
  year: string;
}

export const buildCreditPayload = (accessedPronaf: string, records: CreditData[]) => {
  return {
    accessed_credit_outside_pronaf: accessedPronaf === 'sim',
    credit_contracts: accessedPronaf === 'sim'
      ? records.map(record => ({
        financial_institution: record.institution,
        credit_line: record.creditLine,
        credit_type: record.creditType,
        contracted_value: parseCurrencyToCents(record.amount),
        contraction_year: parseInt(record.year, 10)
      }))
      : []
  };
};

export const getFilteredCreditLines = (institution: string) => {
  const isSeaf = institution === 'SEAF - ESTADUAL';
  const isOther = !!institution && !isSeaf;
  if (isSeaf) return ["FUNDAAF"];
  if (isOther) return ["RECURSO PRÓPRIO DA INSTITUIÇÃO"];
  return Object.values(CuffaCreditLineEnum);
};

export const getFilteredCreditTypes = (institution: string) => {
  const isSeaf = institution === 'SEAF - ESTADUAL';
  const isOther = !!institution && !isSeaf;
  if (isSeaf) return ["INCLUSÃO RURAL – FUNDAAF", "INCENTIVO PRODUTIVO – FUNDAAF"];
  if (isOther) return ["RECURSO PRÓPRIO DA INSTITUIÇÃO"];
  return Object.values(CuffaCreditTypeEnum);
};

interface CreditContract {
  financial_institution: string;
  credit_line: string;
  credit_type: string;
  contracted_value: number;
}

// --- Funções Utilitárias ---
export const formatCentsToCurrency = (cents: number): string => {
  if (typeof cents !== 'number' || isNaN(cents)) return '';
  const realValue = cents / 100;
  return realValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export const parseCurrencyToCents = (currency: string): number => {
  const onlyDigits = currency.replace(/\D/g, '');
  if (onlyDigits === '') return 0;
  return parseInt(onlyDigits, 10);
};

const CreditRuralForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams<{ uuid: string }>();
  const { salvarDadosParciais } = useCuffaStore();

  const [snackbar, setSnackbar] = useState({
    open: false, message: '', severity: 'success' as 'success' | 'error' | 'info'
  });


  const [initialLoading, setInitialLoading] = useState(true);
  const [accessedPronaf, setAccessedPronaf] = useState<string>('');
  const [records, setRecords] = useState<CreditData[]>([]);
  const [formData, setFormData] = useState<CreditData>({
    institution: '', creditLine: '', creditType: '', amount: '', year: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!uuid) {
        setSnackbar({ open: true, message: 'UUID não encontrado.', severity: 'error' });
        setInitialLoading(false);
        return;
      }
      // Primeiro, tentar restaurar do localStorage (chave específica do uuid, depois chave global)
      try {
        const storageKeys: string[] = [];
        if (uuid) storageKeys.push(`cuffa-storage-${uuid}`);
        storageKeys.push('cuffa-storage');

        for (const storageKey of storageKeys) {
          try {
            const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
            if (!raw) continue;
            const parsed = JSON.parse(raw);
            const saved = parsed?.state?.dadosParciais?.creditoFinanciamento;
            if (!saved) continue;

            // Pode conter a forma salva pelo front ou o payload no formato da API
            if (typeof saved.accessedPronaf === 'string') {
              setAccessedPronaf(saved.accessedPronaf);
            } else if (typeof saved.accessed_credit_outside_pronaf === 'boolean') {
              setAccessedPronaf(saved.accessed_credit_outside_pronaf ? 'sim' : 'nao');
            }

            if (Array.isArray(saved.records)) {
              setRecords(saved.records.map((r: any) => ({
                institution: r.institution || r.financial_institution || '',
                creditLine: r.creditLine || r.credit_line || '',
                creditType: r.creditType || r.credit_type || '',
                amount: (typeof r.amount === 'string' && r.amount) || (typeof r.contracted_value === 'number' ? formatCentsToCurrency(r.contracted_value) : ''),
                year: r.year || r.contraction_year || ''
              })));
            } else if (Array.isArray(saved.credit_contracts)) {
              setRecords(saved.credit_contracts.map((c: any) => ({
                institution: c.financial_institution || '',
                creditLine: c.credit_line || '',
                creditType: c.credit_type || '',
                amount: typeof c.contracted_value === 'number' ? formatCentsToCurrency(c.contracted_value) : (c.contracted_value || ''),
                year: c.contraction_year || ''
              })));
            }

            setInitialLoading(false);
            return;
          } catch (e) {
            console.warn(`Falha ao parsear ${storageKey} ao restaurar creditoFinanciamento:`, e);
            continue;
          }
        }
      } catch (e) {
        console.warn('Erro ao acessar localStorage cuffa-storage ao restaurar creditoFinanciamento', e);
      }

      // Se não encontrou no storage, buscar pela API
      try {
        const response = await CuffaAPI.getDataByID(uuid);
        // AJUSTE: Corrigido o caminho para acessar os dados na resposta da API.
        const creditData = response.dados;

        if (creditData) {
          setAccessedPronaf(creditData.accessed_credit_outside_pronaf ? 'sim' : 'nao');

          const loadedRecords = (creditData.credit_contracts || []).map((contract: any) => ({
            institution: contract.financial_institution,
            creditLine: contract.credit_line,
            creditType: contract.credit_type,
            // Supondo que o valor da API vem em centavos
            amount: formatCentsToCurrency(contract.contracted_value),
            year: contract.contraction_year || ''
          }));
          setRecords(loadedRecords);
        }
      } catch (error) {
        console.error("Erro ao carregar dados de crédito:", error);
        setSnackbar({ open: true, message: 'Falha ao carregar os dados. Tente novamente.', severity: 'error' });
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, [uuid]);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const onlyDigits = value.replace(/\D/g, '');
    const formattedValue = formatCentsToCurrency(parseInt(onlyDigits, 10) || 0);
    setFormData({ ...formData, amount: formattedValue });
  };

  const handleChange = (field: keyof CreditData) => (event: SelectChangeEvent<string>) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  // Generate year options: current year and last 5 years
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 6; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  const addRecord = () => {
    if (!formData.institution || !formData.creditLine || !formData.creditType || parseCurrencyToCents(formData.amount) === 0 || !formData.year) {
      setSnackbar({ open: true, message: 'Preencha todos os campos do novo registro com valores válidos.', severity: 'error' });
      return;
    }
    setRecords([...records, formData]);
    setFormData({ institution: '', creditLine: '', creditType: '', amount: '', year: '' });
  };

  const removeRecord = (index: number) => {
    setRecords(records.filter((_, i) => i !== index));
  };

  const handleSave = async (navigateNext = false) => {
    if (!uuid) {
      setSnackbar({ open: true, message: 'Erro: Identificador do formulário não encontrado.', severity: 'error' });
      return;
    }
    if (accessedPronaf === '') {
      setSnackbar({ open: true, message: 'Responda à pergunta "Acessou crédito fora do PRONAF?"', severity: 'error' });
      return;
    }
    if (accessedPronaf === 'sim' && records.length === 0) {
      setSnackbar({ open: true, message: 'Se "Sim", é obrigatório adicionar pelo menos um registro de crédito.', severity: 'error' });
      return;
    }

    setIsSaving(true);
    const payload = {
      accessed_credit_outside_pronaf: accessedPronaf === 'sim',
      credit_contracts: accessedPronaf === 'sim'
        ? records.map(record => ({
          financial_institution: record.institution,
          credit_line: record.creditLine,
          credit_type: record.creditType,
          contracted_value: parseCurrencyToCents(record.amount),
          contraction_year: parseInt(record.year, 10)
        }))
        : []
    };

    try {
      await CuffaAPI.updateCreditAccess(uuid, payload);
      salvarDadosParciais({
        creditoFinanciamento: {
          accessedPronaf: accessedPronaf,
          records: records,
          ...payload
        },
        ultimaRota: location.pathname
      });
      setSnackbar({ open: true, message: 'Dados salvos com sucesso!', severity: 'success' });
      if (navigateNext) {
        setTimeout(() => {
          navigate(ROUTES.FONTES_RENDA_FAMILIA_CUFFA_INCLUIR.replace(':uuid', uuid));
        }, 1000);
      }
    } catch (error) {
      console.error("Erro ao salvar dados de crédito:", error);
      setSnackbar({ open: true, message: 'Falha ao salvar os dados. Tente novamente.', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const setField = (field: keyof CreditData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isSeafEstadual = formData.institution === "SEAF - ESTADUAL";

  const isOtherInstitution = formData.institution && !isSeafEstadual;

  const filteredCreditLines = isSeafEstadual
    ? ["FUNDAAF"]
    : isOtherInstitution
      ? ["RECURSO PRÓPRIO DA INSTITUIÇÃO"]
      : Object.values(CuffaCreditLineEnum);


  const filteredCreditTypes = isSeafEstadual
    ? ["INCLUSÃO RURAL – FUNDAAF", "INCENTIVO PRODUTIVO – FUNDAAF"]
    : isOtherInstitution
      ? ["RECURSO PRÓPRIO DA INSTITUIÇÃO"]
      : Object.values(CuffaCreditTypeEnum);



  useEffect(() => {
    if (isSeafEstadual) {
      setField("creditLine", "FUNDAAF");
      setField("creditType", "");
      return;
    }

    if (isOtherInstitution) {
      setField("creditLine", "RECURSO PRÓPRIO DA INSTITUIÇÃO");
      setField("creditType", "RECURSO PRÓPRIO DA INSTITUIÇÃO");
    }

  }, [formData.institution]);


  if (initialLoading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Crédito e Financiamento" showBackButton />
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
          <Typography ml={2}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Crédito e Financiamento" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title='CRÉDITO E FINANCIAMENTO' width="100%">
            <Box sx={{ marginBottom: '2rem' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                A unidade familiar, nos últimos 5 anos, acessou crédito rural em linhas de crédito que NÃO seja PRONAF? *
              </Typography>
              <RadioGroup
                row
                value={accessedPronaf}
                onChange={(e) => setAccessedPronaf(e.target.value)}
              >
                <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                <FormControlLabel value="nao" control={<Radio />} label="Não" />
              </RadioGroup>
            </Box>

            {accessedPronaf === 'sim' && (
              <>
                <Box sx={{ backgroundColor: '#f5faff', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Novo Registro de Crédito
                  </Typography>
                  <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Instituição Financeira *</InputLabel>
                        <Select value={formData.institution} onChange={handleChange('institution')} label="Instituição Financeira *">
                          {Object.values(CuffaFinancialInstitutionEnum).map((inst) => (
                            <MenuItem key={inst} value={inst}>{inst}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <FormControl fullWidth size="small">
                          <InputLabel>Linha de Crédito *</InputLabel>

                          <Select
                            value={formData.creditLine}
                            onChange={handleChange('creditLine')}
                            label="Linha de Crédito *"
                          >
                            {filteredCreditLines.map((line) => (
                              <MenuItem key={line} value={line}>
                                {line}
                              </MenuItem>
                            ))}
                          </Select>

                        </FormControl>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Tipo de Crédito *</InputLabel>

                        <Select
                          value={formData.creditType}
                          onChange={handleChange('creditType')}
                          label="Tipo de Crédito *"
                        >
                          {filteredCreditTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Valor Contratado (R$) *"
                        value={formData.amount}
                        onChange={handleAmountChange}
                        placeholder="R$ 0,00"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Ano da Contratação *</InputLabel>
                        <Select
                          value={formData.year}
                          onChange={handleChange('year')}
                          label="Ano da Contratação *"
                        >
                          {generateYearOptions().map((year) => (
                            <MenuItem key={year} value={year.toString()}>
                              {year}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button variant="contained" startIcon={<Add />} onClick={addRecord}>
                      Adicionar à Tabela
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ marginBottom: '2rem' }}>
                  <Typography variant="h6" fontWeight="bold" display="flex" justifyContent="center" gutterBottom mb={3} mt={3}>
                    REGISTROS DE CRÉDITO ADICIONADOS
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>INSTITUIÇÃO FINANCEIRA</TableCell>
                          <TableCell>LINHA DE CRÉDITO</TableCell>
                          <TableCell>TIPO DE CRÉDITO</TableCell>
                          <TableCell>VALOR CONTRATADO (R$)</TableCell>
                          <TableCell>ANO</TableCell>
                          <TableCell width="50px">AÇÕES</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {records.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">Nenhum registro adicionado</TableCell>
                          </TableRow>
                        ) : (
                          records.map((record, index) => (
                            <TableRow key={index}>
                              <TableCell>{record.institution}</TableCell>
                              <TableCell>{record.creditLine}</TableCell>
                              <TableCell>{record.creditType}</TableCell>
                              <TableCell>{record.amount}</TableCell>
                              <TableCell>{record.year}</TableCell>
                              <TableCell>
                                <IconButton onClick={() => removeRecord(index)}>
                                  <Delete color="error" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            )}

            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button variant="outlined" color="secondary" onClick={() => navigate(ROUTES.CANAIS_COMERCIALIZACAO_CUFFA_INCLUIR.replace(':uuid', uuid ?? ''))} disabled={isSaving}>
                Retornar
              </Button>
              <Button variant="outlined" color="secondary" onClick={() => handleSave(false)} disabled={isSaving}>
                {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Salvar'}
              </Button>
              <Button variant="contained" color="primary" onClick={() => handleSave(true)} disabled={isSaving}>
                {isSaving ? <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} /> : null}
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MenuProvider>
  );
};

export default CreditRuralForm;
